/**
 * Classifying a WHEP signalling response. Extracted from the player hook because
 * this is the part that can fail silently, and it is testable without a browser.
 *
 * ★ THE VERDICT IS CONTENT, NOT STATUS CODE.
 *   A path this SPA does not serve falls through to the index.html fallback, which
 *   answers **200 with HTML**. `res.ok` is then true, the HTML gets handed to
 *   setRemoteDescription, that throws, the tile retries — forever showing
 *   "connecting" while nothing is wrong with the video. The cockpit hit exactly
 *   this and the rule it came away with is the one implemented here: a WHEP answer
 *   always starts with "v=0", and nothing else counts as one.
 *
 * ★ The four failure kinds stay four values, because their remedies are different
 *   (§9b: silence has more than one meaning):
 *     unconfigured  503 — no relay deployed; run deploy_video_relay.sh
 *     unreachable   502 / network error / non-SDP 200 — relay or gateway problem
 *     denied        401 / 403 — token expired; log in again (retrying hides it)
 *     nosignal      signalling worked, media never arrived (decided by the caller,
 *                   by counting frames — not by this function)
 */

export type WhepVerdict =
  | { kind: 'answer'; sdp: string }
  | { kind: 'unconfigured'; detail: string }
  | { kind: 'unreachable'; detail: string }
  | { kind: 'denied'; detail: string };

/** Pull hub's `{"error": "..."}` out of a body, falling back to a raw excerpt. */
export function hubErrorText(body: string): string {
  try {
    const j = JSON.parse(body) as { error?: string };
    if (j?.error) return j.error;
  } catch { /* not JSON — fall through to the excerpt */ }
  return body.slice(0, 160);
}

export function classifyWhepResponse(status: number, body: string): WhepVerdict {
  if (status === 401 || status === 403) return { kind: 'denied', detail: hubErrorText(body) };
  if (status === 503) return { kind: 'unconfigured', detail: hubErrorText(body) };
  if (status < 200 || status >= 300) {
    return { kind: 'unreachable', detail: hubErrorText(body) || `HTTP ${status}` };
  }
  if (!body.trimStart().startsWith('v=0')) {
    return { kind: 'unreachable', detail: '中继答复不是 SDP(可能被网关或 SPA 兜底拦了)' };
  }
  return { kind: 'answer', sdp: body };
}
